import * as Phaser from "phaser"
import { GameState } from "../core/game-state"
import { getHeroColorTheme } from "../core/hero-colors"
import { AUDIO } from "../core/keys"
import { setMusicMenuVolume, setMusicWaveVolume } from "../core/music-volume"
import { THEME } from "../core/theme"
import { OVERDRIVE_DURATION_MS } from "../data/constants"
import type { BumperEffectType, GameData, GameSprite } from "../types"
import { applyLowHealthVignette } from "../ui/neon-fx"
import type { BumperSystem } from "./bumper-system"
import type { CombatBallSystem } from "./combat-ball-system"
import type { VfxManager } from "./vfx-manager"

const COLOR = THEME.colors
const FONT = THEME.fonts

interface PowerFlowOptions {
  scene: Phaser.Scene
  vfx: VfxManager
  getGameData: () => GameData
  getDogSprite: () => GameSprite
  getCombatBallSystem: () => CombatBallSystem
  bumperSystem: BumperSystem
  playSfx: (key: string, volume?: number) => void
  shoot: () => void
  destroyThreatsTouchingPlayer: () => void
  activatePowerMode: (mode: "overdrive", duration: number) => void
  clearPowerMode: () => void
  suppressBumperHitSfx: (durationMs: number) => void
}

export class PowerFlow {
  private readonly options: PowerFlowOptions

  private lastOverdriveHumAt = 0
  private isOverdriveVisualActive = false
  private isLowHealthVisualActive = false

  constructor(options: PowerFlowOptions) {
    this.options = options
  }

  public update(delta: number): void {
    const { getGameData, playSfx, vfx } = this.options
    const gameData = getGameData()
    const player = gameData.player
    const now = this.options.scene.time.now

    if (player.powerMode === null) {
      this.updateLowHealthVisual(gameData, vfx)
      return
    }

    player.powerTimer = Math.max(0, (player.powerTimer ?? 0) - delta)

    if (
      player.powerMode === "overdrive" &&
      now - this.lastOverdriveHumAt >= 700
    ) {
      this.lastOverdriveHumAt = now
      playSfx(AUDIO.PLASMA_RAIL, 0.18)
    }

    if ((player.powerTimer ?? 0) <= 0) {
      this.deactivate()
    }

    this.updateLowHealthVisual(gameData, vfx)
  }

  public activate(effect: BumperEffectType): void {
    const { getGameData, playSfx, bumperSystem, vfx, activatePowerMode } =
      this.options
    const player = getGameData().player
    const currentMode = player.powerMode
    const mode = "overdrive" as const
    const duration = OVERDRIVE_DURATION_MS

    if (currentMode === mode) {
      activatePowerMode(mode, duration)
      player.combo.state = "powered"
      player.combo.hits = 0
      player.combo.timer = duration
      player.combo.nextEffect = "charge"

      const activationColorStr = bumperSystem.getMostFrequentColor(player.combo)
      const activationColor =
        Phaser.Display.Color.HexStringToColor(activationColorStr).color

      vfx.flashScreen(activationColor, 0.22, 140)
      this.showAnnouncement("OVERDRIVE EXTENDED", activationColor)
      return
    }

    this.deactivate(false)

    activatePowerMode(mode, duration)

    const heroId = GameState.instance.character?.id ?? "bella"
    const heroColors = getHeroColorTheme(heroId)

    bumperSystem.setComboPowered(player.combo, duration, effect)

    vfx.flashScreen(heroColors.glow, 0.45, 220)
    vfx.emitOverdriveScreen(heroColors.glow)
    this.isOverdriveVisualActive = true
    setMusicMenuVolume(this.options.scene)
    playSfx(AUDIO.SFX_WARP_JUMP, 1.0)

    this.showAnnouncement("OVERDRIVE!", heroColors.glow)
  }

  public deactivate(resetCombo = true): void {
    const {
      getGameData,
      getCombatBallSystem,
      bumperSystem,
      scene,
      clearPowerMode,
    } = this.options
    const gameData = getGameData()

    if (gameData.player.powerMode === "overdrive") {
      this.options.suppressBumperHitSfx(420)
      bumperSystem.explodeAll()
      getCombatBallSystem().expireAll(true)
      this.options.playSfx(AUDIO.EXPLOSION, 1.2)
      setMusicWaveVolume(scene)
      if (this.isOverdriveVisualActive) {
        this.options.vfx.clearOverdriveScreen()
        this.isOverdriveVisualActive = false
        if (this.isLowHealthVisualActive) {
          applyLowHealthVignette(scene.cameras.main, 1.0)
        }
      }
    }

    clearPowerMode()
    this.lastOverdriveHumAt = 0
    gameData.player.combo.timer = 0

    if (!gameData.player.isInvulnerable) {
      this.options.getDogSprite().clearTint()
    }

    if (resetCombo) {
      bumperSystem.resetCombo(gameData.player.combo)
    }
  }

  private updateLowHealthVisual(
    gameData: import("../types").GameData,
    vfx: VfxManager,
  ): void {
    const hpPct = gameData.player.hp / gameData.player.maxHp
    const isLow = hpPct <= 0.3
    if (isLow && !this.isLowHealthVisualActive) {
      applyLowHealthVignette(this.options.scene.cameras.main, 1.0)
      this.isLowHealthVisualActive = true
    } else if (!isLow && this.isLowHealthVisualActive) {
      if (!this.isOverdriveVisualActive) {
        vfx.clearExternalAndRestoreBase()
      }
      this.isLowHealthVisualActive = false
    }
  }

  public getDuration(_mode: "overdrive"): number {
    return OVERDRIVE_DURATION_MS
  }

  public getPresentation(_mode: "overdrive"): {
    label: string
    color: number
  } {
    return { label: "OVERDRIVE", color: COLOR.magenta }
  }

  public showAnnouncement(label: string, color: number): void {
    const scene = this.options.scene
    const text = scene.add
      .text(480, 180, label, {
        fontSize: "28px",
        fontFamily: FONT.sans,
        fontStyle: "900",
        color: Phaser.Display.Color.IntegerToColor(color).rgba,
        stroke: COLOR.blackText,
        strokeThickness: 4,
        shadow: {
          color: Phaser.Display.Color.IntegerToColor(color).rgba,
          blur: 18,
          fill: true,
        },
        letterSpacing: 3,
      })
      .setOrigin(0.5)
      .setDepth(1200)
      .setScale(0.72)
      .setAlpha(0)

    scene.tweens.add({
      targets: text,
      alpha: 1,
      scale: 1,
      y: 155,
      duration: 160,
      ease: "Back.easeOut",
      yoyo: true,
      hold: 480,
      onComplete: () => text.destroy(),
    })
  }
}
